// src/components/EditUserModal.js
import React, { useState } from 'react';
import { updateDoc, doc, serverTimestamp, deleteField } from 'firebase/firestore';
import { db } from '../firebase';
import { toast } from 'react-toastify';

function EditUserModal({ user, onClose, onUpdate }) {
  const [role, setRole] = useState(user.role || 'user');
  const [subscriptionTier, setSubscriptionTier] = useState(user.subscriptionTier || 'none');

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Create the update object
      const updateData = { role };
      
      // Handle subscription tier changes
      if (subscriptionTier !== (user.subscriptionTier || 'none')) {
        // Add subscription fields based on the selected tier
        updateData.subscriptionTier = subscriptionTier;
        updateData.purchaseDate = serverTimestamp();
        
        if (subscriptionTier === 'lifetime') {
          // For lifetime subscriptions, remove expiration date
          updateData.expirationDate = deleteField();
        } else if (subscriptionTier === 'sixMonth') {
          // For six-month subscriptions, set expiration date to 6 months from now
          const sixMonthsFromNow = new Date();
          sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6);
          updateData.expirationDate = sixMonthsFromNow;
        } else if (subscriptionTier === 'none') {
          // For no subscription, remove expiration date
          updateData.expirationDate = deleteField();
        }
      }
      
      // Update the user document
      await updateDoc(doc(db, 'users', user.id), updateData);
      
      // Return the updated user data
      onUpdate({ 
        ...user, 
        role, 
        subscriptionTier,
        purchaseDate: new Date(),
        expirationDate: subscriptionTier === 'sixMonth' ? new Date(new Date().setMonth(new Date().getMonth() + 6)) : null
      });
      
      onClose();
      toast.success('User updated successfully.');
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user.');
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white p-6 rounded-lg w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Edit User</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            &times;
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Email:</label>
            <p className="text-gray-900 py-2 px-3 bg-gray-100 rounded">{user.email}</p>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Role:</label>
            <select 
              value={role} 
              onChange={(e) => setRole(e.target.value)} 
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Subscription Tier:</label>
            <select 
              value={subscriptionTier} 
              onChange={(e) => setSubscriptionTier(e.target.value)} 
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              <option value="none">None</option>
              <option value="sixMonth">Six Month</option>
              <option value="lifetime">Lifetime</option>
            </select>
            {subscriptionTier !== (user.subscriptionTier || 'none') && (
              <p className="mt-1 text-sm text-yellow-600">
                Warning: Changing subscription tier will update purchase date and expiration date.
              </p>
            )}
          </div>
          
          <div className="flex justify-end space-x-3">
            <button 
              type="button" 
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditUserModal;
